import React, { useState, useEffect, useCallback } from 'react';
import Footer from '../components/Footer';
import Select from '../components/Select';
import TimeInput from '../components/TimeInput';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';
import { timesheetService, usersService } from '../services/api';

// ── Time helpers ──────────────────────────────────────────────────────────────
const fmt12 = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const minsOf = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const hm = (mins) => {
  if (!mins || mins <= 0) return null;
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
};

// Format total seconds → "Xh Ym Zs" for the live counter
const hms = (totalSecs) => {
  if (totalSecs === null || totalSecs < 0) return '0s';
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const grossMins = (ci, co) => {
  const a = minsOf(ci), b = minsOf(co);
  return a !== null && b !== null && b > a ? b - a : null;
};

const netMins = (ts) => {
  const g = grossMins(ts.checkIn, ts.checkOut);
  if (!g) return null;
  return Math.max(0, g - (ts.lunchMins || 0) - (ts.breakMins || 0));
};

const totalBreakMins = (ts) => (ts.lunchMins || 0) + (ts.breakMins || 0);

// ISO week identifier (Mon–Sun, Thursday decides the year) — used to count how
// many distinct weeks a filtered range actually covers, so the weekly average
// divides by weeks worked rather than by calendar weeks elapsed.
const isoWeekKey = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return null;
  d.setDate(d.getDate() + 4 - (d.getDay() || 7)); // shift to that week's Thursday
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
};

const localISO  = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayStr  = () => localISO(new Date());
const nowTime   = () => new Date().toTimeString().slice(0, 5);

const fmtDate = (s) => s
  ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
  : '';

const fmtDateLong = (s) => s
  ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  : '';

// ── Presets ───────────────────────────────────────────────────────────────────
const PRESETS = [
  { key: 'today',      label: 'Today' },
  { key: 'week',       label: 'This Week' },
  { key: 'month',      label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom',     label: 'Custom' },
];

const presetRange = (key) => {
  const today = new Date();
  if (key === 'today') { const s = localISO(today); return { s, e: s }; }
  if (key === 'week') {
    const dow = today.getDay();
    const mon = new Date(today); mon.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
    const sun = new Date(mon);   sun.setDate(mon.getDate() + 6);
    return { s: localISO(mon), e: localISO(sun) };
  }
  if (key === 'month') {
    return { s: localISO(new Date(today.getFullYear(), today.getMonth(), 1)), e: localISO(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
  }
  if (key === 'last_month') {
    return { s: localISO(new Date(today.getFullYear(), today.getMonth() - 1, 1)), e: localISO(new Date(today.getFullYear(), today.getMonth(), 0)) };
  }
  return null;
};

// Mon–Sun for the week containing `anchor` (defaults to this week), so the
// strip can be paged backwards to reach earlier days.
const getWeekDays = (anchor = new Date()) => {
  const base = new Date(anchor);
  const dow = base.getDay();
  const mon = new Date(base); mon.setDate(base.getDate() - (dow === 0 ? 6 : dow - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon); d.setDate(mon.getDate() + i);
    return { dateStr: localISO(d), label: d.toLocaleDateString('en-GB', { weekday: 'short' }), dayNum: d.getDate() };
  });
};

// ── Live seconds hook — ticks every second ────────────────────────────────────
const useElapsedSecs = (checkInStr) => {
  const [secs, setSecs] = useState(null);
  useEffect(() => {
    if (!checkInStr) { setSecs(null); return; }
    const calc = () => {
      const now = new Date();
      const startSecs = (minsOf(checkInStr) || 0) * 60;
      const nowSecs = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
      setSecs(Math.max(0, nowSecs - startSecs));
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [checkInStr]);
  return secs;
};

const WORKDAY = 8 * 60;

const BREAK_OPTIONS = [
  { value: 0,   label: 'No break' },
  { value: 15,  label: '15 minutes' },
  { value: 30,  label: '30 minutes' },
  { value: 45,  label: '45 minutes' },
  { value: 60,  label: '1 hour' },
  { value: 90,  label: '1 hour 30 min' },
  { value: 120, label: '2 hours' },
];

// ── Component ─────────────────────────────────────────────────────────────────
const Timesheet = () => {
  const { currentUser, showNotification, isSuperAdmin } = useInvoice();
  const confirm    = useConfirm();
  const isEmployee = currentUser?.role === 'employee';
  const isAdmin    = currentUser?.role === 'admin';
  const isSA       = isSuperAdmin();
  // Everyone logs their own hours, not just employees — admins and the super
  // admin also get the check-in/out card in addition to the team view below.
  const canCheckInOut = isEmployee || isAdmin || isSA;

  // A day can now have more than one check-in/out session, so this holds
  // ALL of today's entries — `openEntry` (below) is whichever one, if any,
  // hasn't been checked out yet.
  const [todayEntries, setTodayEntries] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);
  const [weekEntries,  setWeekEntries]  = useState([]);
  // Which week the strip is showing — lets you page back to earlier days
  const [weekAnchor,   setWeekAnchor]   = useState(() => new Date());
  const weekDays = getWeekDays(weekAnchor);

  const [preset,       setPreset]       = useState('month');
  const [startDate,    setStartDate]    = useState(() => presetRange('month').s);
  const [endDate,      setEndDate]      = useState(() => presetRange('month').e);
  const [filterUserId, setFilterUserId] = useState('');

  const [timesheets,   setTimesheets]   = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [allUsers,    setAllUsers]    = useState([]);
  const [accessList,  setAccessList]  = useState([]);

  // ── Checkout modal ────────────────────────────────────────────────────────
  const [checkoutModal,  setCheckoutModal]  = useState(false);
  const [checkoutForm,   setCheckoutForm]   = useState({ checkOutTime: '', accomplishments: '', lunchMins: 60, breakMins: 0 });
  const [checkoutSaving, setCheckoutSaving] = useState(false);

  // ── View modal ────────────────────────────────────────────────────────────
  const [viewingTs, setViewingTs] = useState(null);

  // ── Edit modal ────────────────────────────────────────────────────────────
  // `newSessions` holds extra check-in/out pairs being added for the same
  // date (and same employee as the entry being edited) — filled in and
  // created alongside saving the entry itself, so backfilling a missed
  // second session doesn't require leaving this modal.
  const [editModal,   setEditModal]   = useState(null);
  const [editForm,    setEditForm]    = useState({ checkIn: '', checkOut: '', notes: '', lunchMins: 0, breakMins: 0, newSessions: [] });
  const [editSaving,  setEditSaving]  = useState(false);

  // ── Access modal ──────────────────────────────────────────────────────────
  const [accessModal,  setAccessModal]  = useState(null);
  const [accessSaving, setAccessSaving] = useState(false);

  useEffect(() => {
    const anyOpen = checkoutModal || editModal || viewingTs || accessModal;
    if (!anyOpen) return;
    const fn = (e) => {
      if (e.key !== 'Escape') return;
      if (accessModal) setAccessModal(null);
      else if (viewingTs) setViewingTs(null);
      else if (editModal) setEditModal(null);
      else if (checkoutModal) setCheckoutModal(false);
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [checkoutModal, editModal, viewingTs, accessModal]);

  // The currently open session today (no checkOut yet), if any
  const openEntry = todayEntries.find(t => !t.checkOut) || null;
  // Sessions already completed today, earliest first
  const completedToday = todayEntries.filter(t => t.checkOut).sort((a, b) => a.checkIn.localeCompare(b.checkIn));

  const elapsedSecs = useElapsedSecs(openEntry ? openEntry.checkIn : null);

  // ── Loaders ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEmployee) usersService.getUsers().then(d => setAllUsers(d.users || [])).catch(() => {});
  }, [isEmployee]);

  const loadAccess = useCallback(() => {
    if (isSA || isAdmin) timesheetService.getAccess().then(setAccessList).catch(() => {});
  }, [isSA, isAdmin]);
  useEffect(() => { loadAccess(); }, [loadAccess]);

  const loadToday = useCallback(() => {
    if (!canCheckInOut) { setTodayLoading(false); return; }
    setTodayLoading(true);
    timesheetService.getAll({ startDate: todayStr(), endDate: todayStr(), userId: !isEmployee ? currentUser?.id : undefined })
      .then(list => setTodayEntries(list || []))
      .catch(() => setTodayEntries([]))
      .finally(() => setTodayLoading(false));
  }, [canCheckInOut, isEmployee, currentUser?.id]);
  useEffect(() => { loadToday(); }, [loadToday]);

  const loadWeek = useCallback(() => {
    if (!canCheckInOut) return;
    const days = getWeekDays(weekAnchor);
    const s = days[0].dateStr, e = days[6].dateStr;
    timesheetService.getAll({ startDate: s, endDate: e, userId: !isEmployee ? currentUser?.id : undefined }).then(setWeekEntries).catch(() => {});
  }, [canCheckInOut, isEmployee, currentUser?.id, weekAnchor]);
  useEffect(() => { loadWeek(); }, [loadWeek]);

  const loadTimesheets = useCallback(() => {
    setTableLoading(true);
    const params = {};
    if (startDate)    params.startDate = startDate;
    if (endDate)      params.endDate   = endDate;
    if (filterUserId) params.userId    = filterUserId;
    timesheetService.getAll(params)
      .then(setTimesheets)
      .catch(() => showNotification('Error loading timesheets', 'error'))
      .finally(() => setTableLoading(false));
  }, [startDate, endDate, filterUserId, showNotification]);
  useEffect(() => { loadTimesheets(); }, [loadTimesheets]);

  // ── Preset change ──────────────────────────────────────────────────────────
  const handlePreset = (key) => {
    setPreset(key);
    if (key !== 'custom') {
      const r = presetRange(key);
      if (r) { setStartDate(r.s); setEndDate(r.e); }
    }
  };

  // ── Check In ──────────────────────────────────────────────────────────────
  const handleCheckIn = async () => {
    try {
      await timesheetService.create({ date: todayStr(), checkIn: nowTime() });
      loadToday(); loadWeek(); loadTimesheets();
      showNotification(completedToday.length > 0 ? 'Checked in for a new session' : 'Checked in', 'success');
    } catch (err) { showNotification(err.message || 'Check-in failed', 'error'); }
  };

  // ── Open checkout modal ────────────────────────────────────────────────────
  const openCheckoutModal = () => {
    setCheckoutForm({
      checkOutTime: nowTime(),
      accomplishments: openEntry?.notes || '',
      lunchMins: openEntry?.lunchMins || 60,
      breakMins: openEntry?.breakMins || 0,
    });
    setCheckoutModal(true);
  };

  const handleCheckoutSave = async () => {
    if (!checkoutForm.accomplishments.trim()) {
      showNotification("Please fill in today's accomplishments before checking out", 'error');
      return;
    }
    setCheckoutSaving(true);
    try {
      const entry = await timesheetService.update(openEntry.id, {
        checkOut:        checkoutForm.checkOutTime,
        notes:           checkoutForm.accomplishments,
        lunchMins:       checkoutForm.lunchMins,
        breakMins:       checkoutForm.breakMins,
      });
      loadToday(); loadWeek(); loadTimesheets();
      setCheckoutModal(false);
      const net = netMins(entry);
      showNotification(`Checked out successfully${net ? ` · ${hm(net)} logged` : ''}`, 'success');
    } catch (err) { showNotification(err.message || 'Check-out failed', 'error'); }
    finally { setCheckoutSaving(false); }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const openEdit = (ts) => {
    setEditForm({
      checkIn:  ts.checkIn  || '',
      checkOut: ts.checkOut || '',
      notes:    ts.notes    || '',
      lunchMins: ts.lunchMins || 0,
      breakMins: ts.breakMins || 0,
      newSessions: [],
    });
    setEditModal(ts);
  };

  const addSessionRow = () => setEditForm(f => ({ ...f, newSessions: [...f.newSessions, { checkIn: '', checkOut: '' }] }));
  const setSessionRow = (i, field, value) => setEditForm(f => {
    const rows = [...f.newSessions]; rows[i] = { ...rows[i], [field]: value }; return { ...f, newSessions: rows };
  });
  const removeSessionRow = (i) => setEditForm(f => ({ ...f, newSessions: f.newSessions.filter((_, j) => j !== i) }));

  const handleEditSave = async () => {
    if (!editModal) return;
    setEditSaving(true);
    try {
      const { newSessions, ...primary } = editForm;
      const updated = await timesheetService.update(editModal.id, primary);

      // Any extra sessions added for this same date/employee get created
      // alongside the update, so backfilling a second session doesn't
      // require a separate flow.
      const validNew = (newSessions || []).filter(s => s.checkIn);
      for (const s of validNew) {
        await timesheetService.create({
          date: editModal.date, checkIn: s.checkIn,
          checkOut: s.checkOut || undefined,
          userId: editModal.userId,
        });
      }

      if (canCheckInOut && updated.userId === currentUser?.id && updated.date === todayStr()) loadToday();
      loadTimesheets(); loadWeek(); setEditModal(null);
      const net = netMins(updated);
      const extraNote = validNew.length ? ` · +${validNew.length} session${validNew.length > 1 ? 's' : ''} added` : '';
      showNotification(`Entry updated${net ? ` · ${hm(net)} logged` : ''}${extraNote}`, 'success');
    } catch (err) { showNotification(err.message || 'Update failed', 'error'); }
    finally { setEditSaving(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (ts) => {
    const ok = await confirm(`Delete the timesheet entry for ${fmtDate(ts.date)}? This cannot be undone.`, {
      title: 'Delete Entry',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await timesheetService.delete(ts.id);
      if (canCheckInOut && ts.userId === currentUser?.id && ts.date === todayStr()) loadToday();
      loadTimesheets(); loadWeek();
      showNotification('Entry deleted', 'success');
    } catch (err) { showNotification(err.message || 'Delete failed', 'error'); }
  };

  // ── Access modal ──────────────────────────────────────────────────────────
  const openAccessModal = (entry) => setAccessModal({
    adminId: entry.adminId, adminName: entry.adminName,
    selectedIds: entry.employees.map(e => e.id),
  });
  const toggleEmployee = (id) => setAccessModal(prev => ({
    ...prev,
    selectedIds: prev.selectedIds.includes(id)
      ? prev.selectedIds.filter(x => x !== id)
      : [...prev.selectedIds, id],
  }));
  const handleAccessSave = async () => {
    if (!accessModal) return;
    setAccessSaving(true);
    try {
      await timesheetService.setAccess(accessModal.adminId, accessModal.selectedIds);
      loadAccess(); setAccessModal(null);
      showNotification('Access updated', 'success');
    } catch (err) { showNotification(err.message || 'Failed', 'error'); }
    finally { setAccessSaving(false); }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  // Everyone checks in/out here, not just the 'employee' role — admins and the
  // super admin log hours too and show up in the Log below, so the filter has
  // to offer them as well or their rows can't be isolated.
  const dropdownUsers = isSA
    ? allUsers.filter(u =>
        ['employee', 'admin', 'super_admin'].includes(u.role)
        && u.isActive !== false && !u.isArchived)   // don't offer deactivated staff
    : isAdmin ? (accessList.find(a => a.adminId === currentUser?.id)?.employees ?? [])
    : [];
  // Access grants are about who an admin may view, which is an employee-only
  // concept — deliberately narrower than the filter above.
  const employeeUsers = allUsers.filter(u => u.role === 'employee');
  const canEdit = (ts) => isSA || ts.userId === currentUser?.id;

  const totalNetMins = timesheets.reduce((acc, t) => acc + (netMins(t) || 0), 0);
  // A day can have multiple sessions now — count distinct dates, not rows
  const daysWorked   = new Set(timesheets.filter(t => t.checkIn).map(t => t.date)).size;
  const avgMins      = daysWorked > 0 ? Math.round(totalNetMins / daysWorked) : 0;
  const weeksWorked  = new Set(timesheets.filter(t => t.checkIn).map(t => isoWeekKey(t.date)).filter(Boolean)).size;
  const avgWeekMins  = weeksWorked > 0 ? Math.round(totalNetMins / weeksWorked) : 0;

  const today = todayStr();
  const elapsedMinsNow = elapsedSecs !== null ? Math.floor(elapsedSecs / 60) : 0;
  // Use the open entry's own recorded breaks (usually none yet), not the
  // checkout modal's pending default — that form pre-fills a 60min lunch
  // before it's even opened, which made this go negative right after
  // checking in.
  const progressMins = elapsedMinsNow - ((openEntry?.lunchMins || 0) + (openEntry?.breakMins || 0));
  const progressPct = Math.max(0, Math.min(100, Math.round(((progressMins || 0) / WORKDAY) * 100)));

  // Checkout modal preview
  const coGross = openEntry
    ? grossMins(openEntry.checkIn, checkoutForm.checkOutTime)
    : null;
  const coBreaks = (checkoutForm.lunchMins || 0) + (checkoutForm.breakMins || 0);
  const coNet = coGross !== null ? Math.max(0, coGross - coBreaks) : null;

  const thStyle = {
    padding: '8px 12px', textAlign: 'left',
    fontSize: '11px', fontWeight: '600',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '2px solid var(--color-gray-200)',
  };
  const tdStyle = { padding: '10px 12px' };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <main className="container" style={{ flex: 1, padding: 'var(--spacing-lg) var(--spacing-md)', width: '100%', boxSizing: 'border-box' }}>



      <div style={{ marginBottom: 'var(--spacing-md)', display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: '700', margin: 0 }}>Timesheets</h1>
        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
          {isEmployee ? 'Track your working hours' : canCheckInOut ? 'Track your hours & team attendance' : 'Team attendance and hours'}
        </span>
      </div>

      {/* ── Today card — anyone who logs their own hours ─────────────────── */}
      {canCheckInOut && (
        <div className="card" style={{ marginBottom: 'var(--spacing-md)', position: 'relative', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: openEntry
              ? 'linear-gradient(90deg,#F59E0B,#D97706)'
              : completedToday.length > 0 ? 'linear-gradient(90deg,#22C55E,#16A34A)'
              : 'var(--color-gray-200)',
          }} />

          <div style={{ paddingTop: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
              <div>
                <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: '2px' }}>Today</div>
                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '600' }}>{fmtDateLong(today)}</div>
              </div>
              {openEntry && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '2px', padding: '4px 12px' }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#F59E0B', display: 'inline-block', animation: 'ts-pulse 1.6s ease-in-out infinite' }} />
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', letterSpacing: '0.06em' }}>IN PROGRESS</span>
                </div>
              )}
              {!openEntry && completedToday.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '2px', padding: '4px 12px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#166534', letterSpacing: '0.06em' }}>
                    {hm(completedToday.reduce((s, t) => s + (netMins(t) || 0), 0)) || '0m'} LOGGED
                  </span>
                </div>
              )}
            </div>

            {todayLoading ? <div className="spinner" /> : openEntry ? (
              <div>
                <div style={{ display: 'flex', gap: '40px', marginBottom: 'var(--spacing-md)', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-secondary)', marginBottom: '2px' }}>Checked In</div>
                    <div style={{ fontSize: 'var(--font-size-2xl)', fontFamily: 'monospace', fontWeight: '700', color: 'var(--color-primary)' }}>{fmt12(openEntry.checkIn)}</div>
                  </div>
                  {elapsedSecs !== null && (
                    <div>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-secondary)', marginBottom: '2px' }}>Elapsed</div>
                      <div style={{ fontSize: 'var(--font-size-2xl)', fontFamily: 'monospace', fontWeight: '700', color: '#D97706', minWidth: '130px' }}>
                        {hms(elapsedSecs)}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Progress toward 8h</span>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>{progressPct}%</span>
                  </div>
                  <div style={{ height: '7px', borderRadius: '2px', background: 'var(--color-gray-100)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progressPct}%`, background: progressPct >= 100 ? '#22C55E' : progressPct >= 50 ? '#F59E0B' : 'var(--color-primary)', borderRadius: '2px', transition: 'width 0.6s ease' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                    <button className="btn btn-primary" onClick={openCheckoutModal}>■ Check Out</button>
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(openEntry)}>Edit</button>
                  </div>
              </div>

            ) : (
              <div>
                {completedToday.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>You haven't checked in yet.</span>
                    <button className="btn btn-primary" onClick={handleCheckIn}>▶ Check In</button>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: 'var(--spacing-md)' }}>
                      {completedToday.map(ts => (
                        <div key={ts.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-md)', flexWrap: 'wrap', padding: '10px 14px', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', marginBottom: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}>
                            <span>{fmt12(ts.checkIn)}</span>
                            <span style={{ color: 'var(--color-text-secondary)' }}>→</span>
                            <span>{fmt12(ts.checkOut)}</span>
                            <span style={{ color: '#16A34A', fontWeight: '700' }}>{hm(netMins(ts)) || '—'}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button className="btn btn-outline btn-sm" onClick={() => openEdit(ts)}>Edit</button>
                            <button className="btn btn-error btn-sm" onClick={() => handleDelete(ts)}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button className="btn btn-primary" onClick={handleCheckIn}>▶ Check In Again</button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}


      {/* ── Stats ─────────────────────────────────────────────────────────── */}
      {timesheets.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
          {[
            { label: 'Days Tracked', value: daysWorked },
            { label: 'Net Hours',    value: hm(totalNetMins) || '—' },
            { label: 'Avg / Day',    value: hm(avgMins)       || '—' },
            { label: 'Avg / Week',   value: hm(avgWeekMins)   || '—',
              hint: weeksWorked > 0 ? `across ${weeksWorked} week${weeksWorked !== 1 ? 's' : ''}` : null },
          ].map(({ label, value, hint }) => (
            <div key={label} className="card" style={{ padding: 'var(--spacing-sm)', textAlign: 'center', marginBottom: 0 }} title={hint || undefined}>
              <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', marginBottom: '2px' }}>{value}</div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-text-secondary)', fontWeight: '600' }}>{label}</div>
              {hint && <div style={{ fontSize: '9px', color: 'var(--color-text-secondary)', marginTop: '2px', opacity: 0.75 }}>{hint}</div>}
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: (preset === 'custom' || ((isAdmin || isSA) && dropdownUsers.length > 0)) ? 'var(--spacing-md)' : 0 }}>
          {PRESETS.map(({ key, label }) => (
            <button key={key} onClick={() => handlePreset(key)} style={{
              padding: '5px 14px', borderRadius: '2px',
              border: `1.5px solid ${preset === key ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
              background: preset === key ? 'rgba(185,28,28,0.08)' : 'transparent',
              color: preset === key ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              fontWeight: preset === key ? '600' : '400',
              fontSize: 'var(--font-size-sm)', cursor: 'pointer',
              transition: 'all 0.15s ease', whiteSpace: 'nowrap',
            }}>
              {label}
            </button>
          ))}
        </div>
        {(preset === 'custom' || ((isAdmin || isSA) && dropdownUsers.length > 0)) && (
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {preset === 'custom' && (
              <>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">Start Date</label>
                  <input type="date" className="form-input" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ height: '42px' }} />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">End Date</label>
                  <input type="date" className="form-input" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ height: '42px' }} />
                </div>
              </>
            )}
            {(isAdmin || isSA) && dropdownUsers.length > 0 && (
              <div style={{ minWidth: '160px' }}>
                <label className="form-label">Employee</label>
                <Select
                  value={filterUserId}
                  onChange={v => setFilterUserId(v)}
                  options={[
                    { value: '', label: 'All employees' },
                    ...dropdownUsers.map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}` })),
                  ]}
                />
              </div>
            )}
          </div>
        )}

      {/* ── Week strip — same card as the filters above: both control the Log ─ */}
      {canCheckInOut && (
        <div style={{ borderTop: '1px solid var(--color-gray-200)', marginTop: 'var(--spacing-md)', paddingTop: 'var(--spacing-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', gap: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-secondary)' }}>
              {(() => {
                const thisMon = getWeekDays()[0].dateStr;
                if (weekDays[0].dateStr === thisMon) return 'This Week';
                const a = new Date(weekDays[0].dateStr + 'T00:00:00');
                const b = new Date(weekDays[6].dateStr + 'T00:00:00');
                const f = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                return `${f(a)} – ${f(b)}`;
              })()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setWeekAnchor(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}
                title="Previous week"
                style={{ border: '1px solid var(--color-gray-200)', background: 'transparent', borderRadius: '4px', cursor: 'pointer', padding: '1px 8px', fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}
              >‹</button>
              {getWeekDays()[0].dateStr !== weekDays[0].dateStr && (
                <button
                  onClick={() => setWeekAnchor(new Date())}
                  style={{ border: '1px solid var(--color-gray-200)', background: 'transparent', borderRadius: '4px', cursor: 'pointer', padding: '1px 9px', fontSize: '11px', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}
                >Today</button>
              )}
              <button
                onClick={() => setWeekAnchor(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}
                disabled={getWeekDays()[0].dateStr === weekDays[0].dateStr}
                title="Next week"
                style={{ border: '1px solid var(--color-gray-200)', background: 'transparent', borderRadius: '4px', cursor: getWeekDays()[0].dateStr === weekDays[0].dateStr ? 'not-allowed' : 'pointer', padding: '1px 8px', fontSize: '13px', color: 'var(--color-text-secondary)', opacity: getWeekDays()[0].dateStr === weekDays[0].dateStr ? 0.35 : 1, lineHeight: 1.5 }}
              >›</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
            {weekDays.map(({ dateStr, label, dayNum }, idx) => {
              // A day can have multiple sessions now — aggregate them
              const dayEntries = weekEntries.filter(t => t.date === dateStr);
              const hasOpen    = dayEntries.some(t => !t.checkOut);
              const isToday    = dateStr === today;
              const isFuture   = dateStr > today;
              const nm         = dayEntries.length > 0 ? dayEntries.reduce((s, t) => s + (netMins(t) || 0), 0) : null;
              const pct        = nm ? Math.min(100, Math.round(nm / WORKDAY * 100)) : 0;
              const barColor   = !hasOpen ? (pct >= 100 ? '#22C55E' : '#60A5FA') : '#F59E0B';

              // Clicking a day drills the log below into just that date
              const isSelected = preset === 'custom' && startDate === dateStr && endDate === dateStr;
              const selectDay = () => {
                if (isFuture) return;
                if (isSelected) { setPreset('month'); const r = presetRange('month'); setStartDate(r.s); setEndDate(r.e); return; }
                setPreset('custom'); setStartDate(dateStr); setEndDate(dateStr);
              };

              return (
                <div
                  key={dateStr}
                  onClick={selectDay}
                  title={isFuture ? undefined : (isSelected ? 'Click again to clear' : `View ${fmtDateLong(dateStr)}`)}
                  style={{
                    textAlign: 'center', padding: '5px 4px', borderRadius: 'var(--radius-sm)',
                    cursor: isFuture ? 'default' : 'pointer',
                    background: isSelected ? 'rgba(37,99,235,0.10)' : isToday ? 'rgba(185,28,28,0.06)' : 'transparent',
                    border: `1px solid ${isSelected ? 'rgba(37,99,235,0.45)' : isToday ? 'rgba(185,28,28,0.2)' : 'transparent'}`,
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', color: isSelected ? '#1D4ED8' : isToday ? 'var(--color-primary)' : 'var(--color-text-secondary)', fontWeight: isToday || isSelected ? '700' : '500' }}>{label}</div>
                  <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', color: isSelected ? '#1D4ED8' : isToday ? 'var(--color-primary)' : 'inherit', marginBottom: '4px' }}>{dayNum}</div>
                  <div style={{ height: '24px', borderRadius: '2px', background: 'var(--color-gray-100)', overflow: 'hidden', position: 'relative' }}>
                    {!isFuture && pct > 0 && (
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${pct}%`, background: barColor, borderRadius: '2px 2px 0 0', transition: 'height 0.4s ease' }} />
                    )}
                  </div>
                  <div style={{ fontSize: '9px', marginTop: '2px', color: 'var(--color-text-secondary)', fontFamily: 'monospace', minHeight: '11px' }}>
                    {dayEntries.length > 0 ? (hasOpen ? '●' : (hm(nm) || '—')) : (!isFuture ? '—' : '')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>

      {/* ── Log table ─────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 'var(--spacing-lg)', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>Log</h2>
          {!tableLoading && <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{timesheets.length} {timesheets.length === 1 ? 'entry' : 'entries'}</span>}
        </div>

        {tableLoading ? (
          <div className="flex-center" style={{ padding: 'var(--spacing-xl)' }}><div className="spinner" /></div>
        ) : timesheets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--spacing-xl) 0', color: 'var(--color-text-secondary)' }}>
            <div style={{ fontSize: '2.2rem', marginBottom: '8px', opacity: 0.4 }}>📋</div>
            <p>No entries for this period.</p>
          </div>
        ) : (
          <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                {(isAdmin || isSA) && <th style={thStyle}>Employee</th>}
                <th style={thStyle}>In</th>
                <th style={thStyle}>Out</th>
                <th style={thStyle}>Net Hours</th>
                <th style={thStyle}>Breaks</th>
                <th style={thStyle}>Accomplishments</th>
                <th style={{ ...thStyle, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {timesheets.map((ts) => {
                const nm      = netMins(ts);
                const gm      = grossMins(ts.checkIn, ts.checkOut);
                const breaks  = totalBreakMins(ts);
                const pct     = nm ? Math.min(100, Math.round(nm / WORKDAY * 100)) : 0;
                const isRow   = ts.date === today;

                return (
                  <tr
                    key={ts.id}
                    onClick={() => setViewingTs(ts)}
                    style={{ borderBottom: '1px solid var(--color-gray-100)', background: isRow ? 'rgba(185,28,28,0.025)' : 'transparent', cursor: 'pointer' }}
                    onMouseEnter={e => { if (!isRow) e.currentTarget.style.background = 'var(--color-gray-50)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isRow ? 'rgba(185,28,28,0.025)' : 'transparent'; }}
                  >
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <span style={{ fontWeight: isRow ? '600' : 'normal', color: isRow ? 'var(--color-primary)' : 'inherit' }}>{fmtDate(ts.date)}</span>
                        {isRow && <span style={{ fontSize: '10px', background: 'rgba(185,28,28,0.1)', color: 'var(--color-primary)', padding: '1px 7px', borderRadius: '2px', fontWeight: '700' }}>today</span>}
                      </div>
                    </td>

                    {(isAdmin || isSA) && (
                      <td style={tdStyle}>{ts.user ? `${ts.user.firstName} ${ts.user.lastName || ''}`.trim() : '—'}</td>
                    )}

                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{fmt12(ts.checkIn)}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', color: ts.checkOut ? 'inherit' : 'var(--color-text-secondary)' }}>{fmt12(ts.checkOut)}</td>

                    <td style={{ ...tdStyle, minWidth: '110px' }}>
                      {nm ? (
                        <div>
                          <span style={{ fontWeight: '600', color: pct >= 100 ? '#16A34A' : 'inherit', display: 'block', marginBottom: '4px' }}>{hm(nm)}</span>
                          <div style={{ height: '3px', width: '72px', borderRadius: '2px', background: 'var(--color-gray-100)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#22C55E' : pct >= 75 ? '#60A5FA' : pct >= 50 ? '#F59E0B' : 'var(--color-primary)', borderRadius: '2px' }} />
                          </div>
                          {gm && breaks > 0 && (
                            <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>gross {hm(gm)}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                          {ts.checkIn && !ts.checkOut ? '● in progress' : '—'}
                        </span>
                      )}
                    </td>

                    <td style={tdStyle}>
                      {breaks > 0 ? (
                        <div style={{ fontSize: 'var(--font-size-xs)' }}>
                          {ts.lunchMins > 0 && <div>🍽 {hm(ts.lunchMins)}</div>}
                          {ts.breakMins > 0 && <div>☕ {hm(ts.breakMins)}</div>}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--color-gray-300)' }}>—</span>
                      )}
                    </td>

                    <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ts.notes || ''}>
                      {ts.notes
                        ? <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>{ts.notes}</span>
                        : <span style={{ color: 'var(--color-gray-300)' }}>—</span>}
                    </td>

                    <td style={{ ...tdStyle, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      {canEdit(ts) && (
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(ts)}>Edit</button>
                          <button className="btn btn-error btn-sm" onClick={() => handleDelete(ts)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Admin access management ───────────────────────────────────────── */}
      {isSA && (
        <div className="card">
          <h2 style={{ fontSize: 'var(--font-size-lg)', marginBottom: '4px' }}>Admin Access</h2>
          <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-md)' }}>Configure which employees each admin can view timesheets for.</p>
          {accessList.length === 0 ? <p className="text-secondary">No admins found.</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
              {accessList.map(entry => (
                <div key={entry.adminId} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', padding: 'var(--spacing-sm) 0', borderBottom: '1px solid var(--color-gray-100)', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '500', minWidth: '140px' }}>{entry.adminName}</span>
                  <div style={{ display: 'flex', gap: 'var(--spacing-xs)', flexWrap: 'wrap', flex: 1 }}>
                    {entry.employees.length === 0
                      ? <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>No employees assigned</span>
                      : entry.employees.map(emp => <span key={emp.id} className="badge badge-secondary">{emp.firstName} {emp.lastName || ''}</span>)}
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => openAccessModal(entry)}>Manage</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </main>
    <Footer />

    {/* ── Checkout modal ─────────────────────────────────────────────────── */}
    {checkoutModal && openEntry && (
      <div className="modal-overlay" onClick={() => setCheckoutModal(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
          <div className="flex-between mb-lg">
            <div>
              <h3 style={{ marginBottom: '2px' }}>Check Out</h3>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{fmtDateLong(today)}</div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setCheckoutModal(false)}>✕</button>
          </div>

          <form onSubmit={e => { e.preventDefault(); handleCheckoutSave(); }}>
          {/* Time row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: 'var(--spacing-lg)', padding: '10px 14px', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}>
            <span>In: <strong style={{ fontFamily: 'monospace' }}>{fmt12(openEntry.checkIn)}</strong></span>
            <span style={{ color: 'var(--color-text-secondary)' }}>→</span>
            <span>Out:</span>
            <TimeInput
              value={checkoutForm.checkOutTime}
              onChange={v => setCheckoutForm(f => ({ ...f, checkOutTime: v }))}
            />
          </div>

          {/* Accomplishments */}
          <div className="form-group">
            <label className="form-label">
              Today's Accomplishments <span style={{ color: 'var(--color-primary)' }}>*</span>
            </label>
            <textarea
              className="form-input"
              rows={4}
              value={checkoutForm.accomplishments}
              onChange={e => setCheckoutForm(f => ({ ...f, accomplishments: e.target.value }))}
              placeholder="What did you accomplish today? Key tasks completed, progress made…"
              style={{ resize: 'vertical' }}
              autoFocus
            />
          </div>

          {/* Breaks */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">🍽 Lunch Break</label>
              <Select
                value={checkoutForm.lunchMins}
                onChange={v => setCheckoutForm(f => ({ ...f, lunchMins: v }))}
                options={BREAK_OPTIONS}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">☕ Other Breaks</label>
              <Select
                value={checkoutForm.breakMins}
                onChange={v => setCheckoutForm(f => ({ ...f, breakMins: v }))}
                options={BREAK_OPTIONS}
              />
            </div>
          </div>

          {/* Summary */}
          {coGross !== null && (
            <div style={{ marginBottom: 'var(--spacing-lg)', padding: '12px 14px', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Gross time</span>
                <span style={{ fontFamily: 'monospace' }}>{hm(coGross) || '—'}</span>
              </div>
              {coBreaks > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Breaks deducted</span>
                  <span style={{ fontFamily: 'monospace', color: 'var(--color-primary)' }}>− {hm(coBreaks)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-gray-200)', paddingTop: '6px', marginTop: '4px', fontWeight: '700' }}>
                <span>Net hours</span>
                <span style={{ fontFamily: 'monospace', color: '#16A34A' }}>{hm(coNet) || '—'}</span>
              </div>
            </div>
          )}

          <div className="flex gap-md">
            <button type="submit" className="btn btn-primary" disabled={checkoutSaving}>
              {checkoutSaving ? 'Saving…' : '■ Check Out'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setCheckoutModal(false)}>Cancel</button>
          </div>
          </form>
        </div>
      </div>
    )}

    {/* ── Edit modal ─────────────────────────────────────────────────────── */}
    {editModal && (
      <div className="modal-overlay" onClick={() => setEditModal(null)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
          <div className="flex-between mb-lg">
            <div>
              <h3 style={{ marginBottom: '2px' }}>Edit Entry</h3>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{fmtDateLong(editModal.date)}</div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditModal(null)}>✕</button>
          </div>

          <form onSubmit={e => { e.preventDefault(); handleEditSave(); }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Check-In</label>
              <TimeInput value={editForm.checkIn}
                onChange={v => setEditForm(f => ({ ...f, checkIn: v }))} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Check-Out</label>
              <TimeInput value={editForm.checkOut}
                onChange={v => setEditForm(f => ({ ...f, checkOut: v }))} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">🍽 Lunch Break</label>
              <Select
                value={editForm.lunchMins}
                onChange={v => setEditForm(f => ({ ...f, lunchMins: v }))}
                options={BREAK_OPTIONS}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">☕ Other Breaks</label>
              <Select
                value={editForm.breakMins}
                onChange={v => setEditForm(f => ({ ...f, breakMins: v }))}
                options={BREAK_OPTIONS}
              />
            </div>
          </div>

          {editForm.checkIn && editForm.checkOut && (() => {
            const fakeTs = { checkIn: editForm.checkIn, checkOut: editForm.checkOut, lunchMins: editForm.lunchMins, breakMins: editForm.breakMins };
            const nm = netMins(fakeTs);
            const gm = grossMins(editForm.checkIn, editForm.checkOut);
            const br = totalBreakMins(fakeTs);
            return nm ? (
              <div style={{ marginBottom: 'var(--spacing-md)', padding: '10px 14px', background: 'rgba(34,197,94,0.08)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', color: '#166534' }}>
                Net: <strong>{hm(nm)}</strong>
                {br > 0 && <span style={{ marginLeft: '8px', color: 'var(--color-text-secondary)' }}>(gross {hm(gm)}, −{hm(br)} breaks)</span>}
              </div>
            ) : null;
          })()}

          {/* Extra sessions — e.g. stepped out and came back later the same day */}
          {editForm.newSessions.map((s, i) => (
            <div key={i} style={{ marginBottom: 'var(--spacing-md)', padding: '12px', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-secondary)' }}>New Session</span>
                <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }} onClick={() => removeSessionRow(i)}>Remove</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Check-In</label>
                  <TimeInput value={s.checkIn} onChange={v => setSessionRow(i, 'checkIn', v)} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Check-Out</label>
                  <TimeInput value={s.checkOut} onChange={v => setSessionRow(i, 'checkOut', v)} />
                </div>
              </div>
              {s.checkIn && s.checkOut && (() => {
                const gm = grossMins(s.checkIn, s.checkOut);
                return gm ? <div style={{ marginTop: '8px', fontSize: 'var(--font-size-xs)', color: '#166534' }}>Net: <strong>{hm(gm)}</strong></div> : null;
              })()}
            </div>
          ))}
          <button type="button" className="btn btn-outline btn-sm" style={{ marginBottom: 'var(--spacing-md)' }} onClick={addSessionRow}>
            + Add another session
          </button>

          <div className="form-group">
            <label className="form-label">Accomplishments</label>
            <textarea className="form-input" rows={3} value={editForm.notes}
              onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="What was accomplished…" style={{ resize: 'vertical' }} />
          </div>

          <div className="flex gap-md">
            <button type="submit" className="btn btn-primary" disabled={editSaving}>
              {editSaving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
          </div>
          </form>
        </div>
      </div>
    )}

    {/* ── View modal ─────────────────────────────────────────────────────── */}
    {viewingTs && (() => {
      const ts = viewingTs;
      const nm = netMins(ts);
      const gm = grossMins(ts.checkIn, ts.checkOut);
      const breaks = totalBreakMins(ts);
      const pct = nm ? Math.min(100, Math.round(nm / WORKDAY * 100)) : 0;
      const isToday = ts.date === today;
      const labelStyle = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '2px' };
      const valueStyle = { fontSize: 'var(--font-size-sm)', fontWeight: '500' };
      return (
        <div className="modal-overlay" onClick={() => setViewingTs(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="flex-between mb-lg">
              <div>
                <h3 style={{ marginBottom: '2px' }}>{fmtDateLong(ts.date)}</h3>
                {isToday && <span style={{ fontSize: '11px', background: 'rgba(185,28,28,0.1)', color: 'var(--color-primary)', padding: '1px 8px', borderRadius: '2px', fontWeight: '700' }}>Today</span>}
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                {canEdit(ts) && (
                  <button className="btn btn-primary btn-sm" onClick={() => { setViewingTs(null); openEdit(ts); }}>Edit</button>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => setViewingTs(null)}>✕</button>
              </div>
            </div>

            {(isAdmin || isSA) && ts.user && (
              <div style={{ marginBottom: 'var(--spacing-md)', padding: '8px 12px', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>
                {ts.user.firstName} {ts.user.lastName || ''}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
              <div>
                <div style={labelStyle}>Check In</div>
                <div style={{ ...valueStyle, fontFamily: 'monospace', fontSize: '1.1rem' }}>{fmt12(ts.checkIn) || '—'}</div>
              </div>
              <div>
                <div style={labelStyle}>Check Out</div>
                <div style={{ ...valueStyle, fontFamily: 'monospace', fontSize: '1.1rem', color: ts.checkOut ? 'inherit' : 'var(--color-text-secondary)' }}>
                  {ts.checkOut ? fmt12(ts.checkOut) : (ts.checkIn ? 'In progress' : '—')}
                </div>
              </div>
            </div>

            {nm ? (
              <div style={{ marginBottom: 'var(--spacing-md)', padding: '12px 14px', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '700', fontSize: '1.2rem', color: pct >= 100 ? '#16A34A' : 'inherit' }}>{hm(nm)} net</span>
                  {gm && breaks > 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>gross {hm(gm)} − {hm(breaks)} breaks</span>}
                </div>
                <div style={{ height: '4px', borderRadius: '2px', background: 'var(--color-gray-200)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#22C55E' : pct >= 75 ? '#60A5FA' : pct >= 50 ? '#F59E0B' : 'var(--color-primary)', borderRadius: '2px', transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>{pct}% of {hm(WORKDAY)} workday</div>
              </div>
            ) : null}

            {breaks > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
                {ts.lunchMins > 0 && (
                  <div>
                    <div style={labelStyle}>Lunch Break</div>
                    <div style={valueStyle}>🍽 {hm(ts.lunchMins)}</div>
                  </div>
                )}
                {ts.breakMins > 0 && (
                  <div>
                    <div style={labelStyle}>Other Breaks</div>
                    <div style={valueStyle}>☕ {hm(ts.breakMins)}</div>
                  </div>
                )}
              </div>
            )}

            <div>
              <div style={labelStyle}>Accomplishments</div>
              {ts.notes
                ? <p style={{ fontSize: 'var(--font-size-sm)', margin: '4px 0 0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ts.notes}</p>
                : <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic', margin: '4px 0 0' }}>No notes recorded.</p>
              }
            </div>
          </div>
        </div>
      );
    })()}

    {/* ── Access modal ───────────────────────────────────────────────────── */}
    {accessModal && (
      <div className="modal-overlay" onClick={() => setAccessModal(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="flex-between mb-lg">
            <div>
              <h3 style={{ marginBottom: '2px' }}>Manage Access</h3>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{accessModal.adminName}</div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setAccessModal(null)}>✕</button>
          </div>
          <form onSubmit={e => { e.preventDefault(); handleAccessSave(); }}>
          <p className="text-secondary" style={{ marginBottom: 'var(--spacing-md)', fontSize: 'var(--font-size-sm)' }}>
            Select employees this admin can view timesheets for:
          </p>
          {employeeUsers.length === 0 ? <p className="text-secondary">No employees found.</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: 'var(--spacing-lg)', maxHeight: '300px', overflowY: 'auto' }}>
              {employeeUsers.map(emp => {
                const checked = accessModal.selectedIds.includes(emp.id);
                return (
                  <label key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer', padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: checked ? 'rgba(185,28,28,0.05)' : 'transparent', border: `1px solid ${checked ? 'rgba(185,28,28,0.18)' : 'transparent'}`, transition: 'all 0.15s ease' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleEmployee(emp.id)} style={{ width: '15px', height: '15px', flexShrink: 0, accentColor: 'var(--color-primary)' }} />
                    <span style={{ flex: 1, fontWeight: checked ? '600' : 'normal' }}>{emp.firstName} {emp.lastName || ''}</span>
                    {emp.employeeNo && <span className="text-secondary" style={{ fontSize: '11px' }}>#{emp.employeeNo}</span>}
                  </label>
                );
              })}
            </div>
          )}
          <div className="flex gap-md">
            <button type="submit" className="btn btn-primary" disabled={accessSaving}>
              {accessSaving ? 'Saving…' : `Save (${accessModal.selectedIds.length} selected)`}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setAccessModal(null)}>Cancel</button>
          </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
};

export default Timesheet;
